import { useState } from "react"
import {
  useGetAdDestinationsQuery,
  type TAdDestination,
} from "~/ui-rtk/api/adDestinationsApi"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import {
  exportTableToCSV,
  type SerializableColumnConfig,
} from "~/ui-rtk/utils/csv-export"

export const RANGE_OPTIONS = [
  { label: "Last 7 days", value: "7d" },
  { label: "Last 14 days", value: "14d" },
  { label: "Last 30 days", value: "30d" },
  { label: "Last 90 days", value: "90d" },
]

export const STATUS_OPTIONS = [
  { label: "Active ads", value: "active" },
  { label: "Paused ads", value: "paused" },
  { label: "All ads", value: "all" },
]

export const CHANNEL_OPTIONS = [
  { label: "All channels", value: "" },
  { label: "Meta", value: "meta" },
  { label: "Google Ads", value: "google_ads" },
  { label: "TikTok Ads", value: "tiktok_ads" },
  { label: "YouTube Ads", value: "youtube_ads" },
]

/** Columns in the CSV hand-off. Keys are `TAdDestination` fields. */
export const CSV_COLUMNS: SerializableColumnConfig[] = [
  { key: "adId", header: "Ad ID", type: "string" },
  { key: "adName", header: "Ad", type: "string" },
  { key: "campaignName", header: "Campaign", type: "string" },
  { key: "channel", header: "Channel", type: "string" },
  { key: "status", header: "Status", type: "string" },
  { key: "url", header: "Destination URL", type: "string" },
  { key: "urlTags", header: "URL Tags", type: "string" },
  { key: "hasUrlTags", header: "Has URL Tags", type: "boolean" },
  { key: "spend", header: "Spend", type: "number" },
  { key: "impressions", header: "Impressions", type: "number" },
  { key: "clicks", header: "Clicks", type: "number" },
  { key: "ctr", header: "CTR %", type: "number" },
]

export default function useConnect() {
  const company = useCurrentCompany()
  const [range, setRange] = useState("30d")
  const [status, setStatus] = useState("active")
  const [channel, setChannel] = useState("")
  const [search, setSearch] = useState("")

  // `/cli/*` resolves the company by slug OR id and checks the session has
  // access either way. The browser only carries the id, so send that.
  const companyId = company?.id ?? ""

  const { data, isFetching, isError, refetch } = useGetAdDestinationsQuery(
    {
      company: companyId,
      range,
      status,
      channel: channel || undefined,
      search,
    },
    { skip: !companyId },
  )

  const ads: TAdDestination[] = data?.ads ?? []

  const exportCsv = () =>
    exportTableToCSV(
      ads.map(ad => ({ ...ad })),
      CSV_COLUMNS,
      { filename: `ad-destinations-${status}-${range}-${Date.now()}.csv` },
    )

  return {
    ads,
    isFetching,
    isError,
    refetch,
    range,
    setRange,
    status,
    setStatus,
    channel,
    setChannel,
    search,
    setSearch,
    exportCsv,
    hasCompany: Boolean(companyId),
  }
}
